/* ============================================================
   Ebook Store Theme — theme.js
   ============================================================ */

'use strict';

/* ---- Helpers -------------------------------------------- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const money = (cents) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);

/* ---- Cart state ----------------------------------------- */
let cartState = null;

async function fetchCart() {
  const res = await fetch('/cart.js');
  cartState = await res.json();
  return cartState;
}

async function addToCart(variantId, qty = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: qty }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.description || window.cartStrings?.error || 'Error adding to cart');
  }
  return await res.json();
}

async function updateCartItem(line, qty) {
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ line, quantity: qty }),
  });
  cartState = await res.json();
  return cartState;
}

/* ---- Toast ---------------------------------------------- */
let toastTimer;
function showToast(msg, duration = 3000) {
  const el = $('#toast');
  if (!el) return;
  el.textContent = msg;
  el.classList.add('toast--visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('toast--visible'), duration);
}

/* ---- Cart drawer ---------------------------------------- */
function updateCartDrawer(cart) {
  const body = $('#cart-drawer-body');
  const footer = $('#cart-drawer-footer');
  const countEl = $('#cart-count');
  const subtotal = $('#cart-subtotal-price');

  if (countEl) {
    countEl.textContent = cart.item_count;
    countEl.setAttribute('aria-label', `${cart.item_count} items in cart`);
  }

  if (!body) return;

  if (cart.item_count === 0) {
    body.innerHTML = '<p class="cart-drawer__empty">Your cart is empty</p>';
    if (footer) footer.hidden = true;
    return;
  }

  if (footer) footer.hidden = false;
  if (subtotal) subtotal.textContent = money(cart.total_price);

  body.innerHTML = cart.items.map((item, i) => `
    <div class="cart-drawer-item" data-line="${i + 1}">
      <a href="${item.url}">
        ${item.image
          ? `<img src="${item.image.replace('_200x', '_120x')}" class="cart-drawer-item__image" alt="${item.title}" width="56" height="75" loading="lazy">`
          : '<div class="cart-drawer-item__image" style="background:var(--color-bg-alt)"></div>'
        }
      </a>
      <div>
        <p class="cart-drawer-item__title">${item.title}</p>
        <div class="cart-drawer-item__meta">
          <span class="cart-drawer-item__price">${money(item.final_price)}</span>
          <button class="cart-drawer-item__remove" data-line="${i + 1}" aria-label="Remove ${item.title}">Remove</button>
        </div>
      </div>
    </div>
  `).join('');

  body.querySelectorAll('.cart-drawer-item__remove').forEach(btn => {
    btn.addEventListener('click', async () => {
      const line = parseInt(btn.dataset.line);
      const cart = await updateCartItem(line, 0);
      updateCartDrawer(cart);
    });
  });
}

function openCartDrawer() {
  const drawer = $('#cart-drawer');
  if (!drawer) return;
  drawer.setAttribute('aria-hidden', 'false');
  $('#cart-toggle')?.setAttribute('aria-expanded', 'true');
  document.body.style.overflow = 'hidden';
  fetchCart().then(updateCartDrawer);
}

function closeCartDrawer() {
  const drawer = $('#cart-drawer');
  if (!drawer) return;
  drawer.setAttribute('aria-hidden', 'true');
  $('#cart-toggle')?.setAttribute('aria-expanded', 'false');
  document.body.style.overflow = '';
}

/* ---- Cart toggle ---------------------------------------- */
$('#cart-toggle')?.addEventListener('click', () => {
  const drawer = $('#cart-drawer');
  const isOpen = drawer?.getAttribute('aria-hidden') === 'false';
  isOpen ? closeCartDrawer() : openCartDrawer();
});

$('#cart-drawer-close')?.addEventListener('click', closeCartDrawer);
$('#cart-drawer .cart-drawer__overlay')?.addEventListener('click', closeCartDrawer);

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    closeCartDrawer();
    closeSearch();
  }
});

/* ---- Add-to-cart (product form) ------------------------- */
const productForm = $('#product-form');
if (productForm) {
  productForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = $('#add-to-cart');
    const variantId = productForm.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    btn.disabled = true;
    const origText = btn.innerHTML;
    btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spin"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0"/></svg> Adding…`;

    try {
      await addToCart(parseInt(variantId));
      const cart = await fetchCart();
      updateCartDrawer(cart);
      openCartDrawer();
      showToast('Added to cart!');
    } catch (err) {
      showToast(err.message, 4000);
    } finally {
      btn.disabled = false;
      btn.innerHTML = origText;
    }
  });
}

/* ---- Quick-add & card cart buttons ---------------------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.product-card__quick-add, .product-card__cart-btn');
  if (!btn) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  btn.disabled = true;
  try {
    await addToCart(parseInt(variantId));
    const cart = await fetchCart();
    updateCartDrawer(cart);
    openCartDrawer();
    showToast(`Added to cart!`);
  } catch (err) {
    showToast(err.message, 4000);
  } finally {
    btn.disabled = false;
  }
});

/* ---- Search toggle -------------------------------------- */
function openSearch() {
  const bar = $('#search-drawer');
  if (!bar) return;
  bar.hidden = false;
  $('#search-toggle')?.setAttribute('aria-expanded', 'true');
  setTimeout(() => $('#search-input')?.focus(), 50);
}
function closeSearch() {
  const bar = $('#search-drawer');
  if (!bar) return;
  bar.hidden = true;
  $('#search-toggle')?.setAttribute('aria-expanded', 'false');
}

$('#search-toggle')?.addEventListener('click', () => {
  const bar = $('#search-drawer');
  bar?.hidden ? openSearch() : closeSearch();
});
$('#search-close')?.addEventListener('click', closeSearch);

/* ---- Mobile menu ---------------------------------------- */
$('#mobile-menu-toggle')?.addEventListener('click', () => {
  const menu = $('#mobile-menu');
  const btn = $('#mobile-menu-toggle');
  const isOpen = !menu?.hidden;
  menu.hidden = isOpen;
  btn?.setAttribute('aria-expanded', String(!isOpen));
});

/* ---- Product gallery thumbnails ------------------------- */
$$('.product-gallery__thumb').forEach(thumb => {
  thumb.addEventListener('click', () => {
    $$('.product-gallery__thumb').forEach(t => t.classList.remove('product-gallery__thumb--active'));
    thumb.classList.add('product-gallery__thumb--active');
    const featImg = $('#product-featured-image');
    if (featImg) {
      featImg.src = thumb.dataset.imageUrl;
    }
  });
});

/* ---- Product tabs --------------------------------------- */
$$('.product-tabs__btn').forEach(btn => {
  btn.addEventListener('click', () => {
    const panelId = btn.getAttribute('aria-controls');
    $$('.product-tabs__btn').forEach(b => {
      b.classList.remove('product-tabs__btn--active');
      b.setAttribute('aria-selected', 'false');
    });
    $$('.product-tabs__panel').forEach(p => {
      p.classList.remove('product-tabs__panel--active');
      p.hidden = true;
    });
    btn.classList.add('product-tabs__btn--active');
    btn.setAttribute('aria-selected', 'true');
    const panel = $(`#${panelId}`);
    if (panel) {
      panel.classList.add('product-tabs__panel--active');
      panel.hidden = false;
    }
  });
});

/* ---- Collection filters panel toggle -------------------- */
$('#filter-toggle')?.addEventListener('click', () => {
  const panel = $('#filter-panel');
  const btn = $('#filter-toggle');
  const isHidden = panel?.hidden;
  panel.hidden = !isHidden;
  btn?.setAttribute('aria-expanded', String(isHidden));
});
$('#filter-close')?.addEventListener('click', () => {
  const panel = $('#filter-panel');
  if (panel) panel.hidden = true;
  $('#filter-toggle')?.setAttribute('aria-expanded', 'false');
});

/* ---- View toggle (grid/list) ---------------------------- */
$$('.view-toggle__btn').forEach(btn => {
  btn.addEventListener('click', () => {
    $$('.view-toggle__btn').forEach(b => {
      b.classList.remove('view-toggle__btn--active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('view-toggle__btn--active');
    btn.setAttribute('aria-pressed', 'true');

    const grid = $('#product-grid');
    if (!grid) return;
    const view = btn.dataset.view;
    if (view === 'list') {
      grid.style.gridTemplateColumns = '1fr';
      $$('.product-card', grid).forEach(card => {
        card.style.flexDirection = 'row';
        card.style.maxHeight = '160px';
      });
    } else {
      grid.style.gridTemplateColumns = '';
      $$('.product-card', grid).forEach(card => {
        card.style.flexDirection = '';
        card.style.maxHeight = '';
      });
    }
  });
});

/* ---- Cart page quantity controls ------------------------ */
$$('.quantity-selector__btn').forEach(btn => {
  btn.addEventListener('click', async () => {
    const line = parseInt(btn.dataset.line);
    const input = $(`.quantity-selector__input[data-line="${line}"]`);
    if (!input) return;
    let qty = parseInt(input.value);
    if (btn.dataset.action === 'increase') qty++;
    else qty = Math.max(0, qty - 1);
    input.value = qty;
    const cart = await updateCartItem(line, qty);
    const subtotal = $('#cart-page-subtotal');
    if (subtotal) subtotal.textContent = money(cart.total_price);
    const count = $('#cart-count');
    if (count) count.textContent = cart.item_count;
    if (qty === 0) location.reload();
  });
});

$$('.cart-item__remove').forEach(btn => {
  btn.addEventListener('click', async () => {
    const line = parseInt(btn.dataset.line);
    await updateCartItem(line, 0);
    location.reload();
  });
});

/* ---- Spinner style for loading state -------------------- */
const spinStyle = document.createElement('style');
spinStyle.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .spin { animation: spin 0.7s linear infinite; }
`;
document.head.appendChild(spinStyle);

/* ---- Init cart count on page load ----------------------- */
fetchCart().then(cart => {
  const count = $('#cart-count');
  if (count) {
    count.textContent = cart.item_count;
    count.setAttribute('aria-label', `${cart.item_count} items in cart`);
  }
});

/* ---- Mega menu ------------------------------------------ */
$$('.site-nav__item--mega .site-nav__mega-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    const panel = $(`#${btn.getAttribute('aria-controls')}`);
    if (!panel) return;
    const isOpen = !panel.hidden;
    panel.hidden = isOpen;
    btn.setAttribute('aria-expanded', String(!isOpen));
  });
});
// Close mega menu on outside click
document.addEventListener('click', (e) => {
  if (!e.target.closest('.site-nav__item--mega')) {
    $$('.mega-menu').forEach(m => { m.hidden = true; });
    $$('.site-nav__item--mega .site-nav__mega-btn').forEach(b => b.setAttribute('aria-expanded', 'false'));
  }
});

/* ---- Mobile accordion ----------------------------------- */
$$('.mobile-menu__link--toggle').forEach(btn => {
  btn.addEventListener('click', () => {
    const target = $(`#${btn.getAttribute('aria-controls')}`);
    if (!target) return;
    const isOpen = !target.hidden;
    target.hidden = isOpen;
    btn.setAttribute('aria-expanded', String(!isOpen));
  });
});
